import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import AdminService from "../../../api/services/adminService";
import { clearAuth } from "../../../utils/authStorage";
import { STAKING_WALLET_HINT, formatWalletType } from "../../../utils/walletTypes";
import {
  SEQUENCE_FALLBACK,
  copyText,
  earningAccordionTitle,
  formatDateTime,
  formatMoney,
  isReleaseJobRunning,
  isUnauthorized,
  releasedByLabel,
  roundTotalsFromPayload,
  userDisplayName,
} from "./roundReleaseLedgerUtils";
import "../Home/Homepage.css";
import "../Trading/AllOpenOrdersPage.css";
import "../Trading/CurrencyPairManagementPage.css";
import "./RoundReleaseLedgerPage.css";

const TABS = [
  { id: "rounds", label: "Rounds" },
  { id: "history", label: "History" },
];

const HISTORY_ROUND_FILTERS = [
  { value: "all", label: "All rounds" },
  { value: 1, label: "Round 1" },
  { value: 2, label: "Round 2" },
  { value: 3, label: "Round 3" },
  { value: 4, label: "Round 4" },
];

const POLL_MS = 2000;
const POLL_MAX = 40;

const RoundReleaseLedgerDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("rounds");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [canRetry, setCanRetry] = useState(false);
  const [selectedCurrencyId, setSelectedCurrencyId] = useState("");
  const [historyRound, setHistoryRound] = useState("all");
  const [polling, setPolling] = useState(false);

  const goBack = useCallback(() => {
    navigate("/dashboard/referral/round-releases");
  }, [navigate]);

  const fetchDetail = useCallback(
    async ({ silent = false } = {}) => {
      if (!userId) {
        toast.error("Invalid user ID format");
        goBack();
        return null;
      }
      if (!silent) {
        setLoading(true);
        setError("");
        setCanRetry(false);
      }
      try {
        const res = await AdminService.getReferralReleaseUserById(userId);
        if (isUnauthorized(res)) {
          clearAuth();
          window.location.href = "/";
          return null;
        }
        const status = Number(res?.httpStatus);
        if (status === 400 || status === 404) {
          toast.error(res?.message || (status === 404 ? "User not found" : "Invalid user ID format"));
          goBack();
          return null;
        }
        if (res?.success === false) {
          if (!silent) {
            setData(null);
            setError(res?.message || "Failed to fetch user round release details");
            setCanRetry(status >= 500 || !status);
          }
          return null;
        }
        const payload = res?.data && !Array.isArray(res.data) ? res.data : null;
        if (!payload) {
          if (!silent) {
            setData(null);
            setError(res?.message || "Failed to fetch user round release details");
            setCanRetry(true);
          }
          return null;
        }
        setData(payload);
        setError("");
        return payload;
      } catch (err) {
        if (!silent) {
          setData(null);
          setError(err?.message || "Failed to fetch user round release details");
          setCanRetry(true);
        }
        return null;
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [userId, goBack]
  );

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  useEffect(() => {
    setHistoryRound("all");
    setSelectedCurrencyId("");
  }, [userId]);

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    let timer = 0;

    const tick = async (fromPoll) => {
      if (cancelled) return;
      try {
        const res = await AdminService.getReferralReleaseRoundNowStatus();
        if (cancelled) return;
        if (isUnauthorized(res)) {
          clearAuth();
          window.location.href = "/";
          return;
        }
        const running = isReleaseJobRunning(res);
        setPolling(running);
        if (!running) {
          if (fromPoll) await fetchDetail({ silent: true });
          return;
        }
        attempts += 1;
        if (attempts >= POLL_MAX) {
          setPolling(false);
          await fetchDetail({ silent: true });
          return;
        }
        timer = window.setTimeout(() => tick(true), POLL_MS);
      } catch {
        if (attempts >= POLL_MAX) setPolling(false);
        else timer = window.setTimeout(() => tick(true), POLL_MS);
      }
    };

    tick(false);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [fetchDetail, userId]);

  const user = data?.user || {};
  const summary = data?.summary || {};
  const sequence = data?.sequence || {};
  const wallets = useMemo(
    () => (Array.isArray(data?.staking_wallet) ? data.staking_wallet : []),
    [data]
  );
  const vestings = useMemo(
    () => (Array.isArray(data?.vestings) ? data.vestings : []),
    [data]
  );
  const history = useMemo(() => {
    const rows = Array.isArray(data?.release_history) ? [...data.release_history] : [];
    return rows.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }, [data]);

  const selectedWallet = useMemo(() => {
    if (!wallets.length) return null;
    return (
      wallets.find(
        (w) => w.currency_id === selectedCurrencyId || w.currency === selectedCurrencyId
      ) || wallets[0]
    );
  }, [wallets, selectedCurrencyId]);

  const currencyCode =
    selectedWallet?.currency || summary.next_round_currency || vestings[0]?.currency || "USDT";

  const groupedRounds = useMemo(() => roundTotalsFromPayload(data), [data]);

  const vestingsByCurrency = useMemo(() => {
    const byCcy = new Map();
    vestings.forEach((v) => {
      const ccy = v.currency || "USDT";
      if (!byCcy.has(ccy)) byCcy.set(ccy, []);
      byCcy.get(ccy).push(v);
    });
    byCcy.forEach((list) => {
      list.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
    });
    return byCcy;
  }, [vestings]);

  const vestingsWithRound = useMemo(() => {
    const rows = [];
    vestingsByCurrency.forEach((list) => {
      list.forEach((v) => rows.push(v));
    });
    return rows;
  }, [vestingsByCurrency]);

  const selectedCurrency = selectedWallet?.currency || "";

  const visibleRoundGroups = useMemo(() => {
    if (!selectedCurrency || wallets.length <= 1) return groupedRounds;
    return groupedRounds.filter((g) => g.currency === selectedCurrency);
  }, [groupedRounds, selectedCurrency, wallets.length]);

  const visibleVestings = useMemo(() => {
    if (!selectedCurrency || wallets.length <= 1) return vestingsWithRound;
    return vestingsWithRound.filter(
      (vesting) => (vesting.currency || "USDT") === selectedCurrency
    );
  }, [vestingsWithRound, selectedCurrency, wallets.length]);

  const filteredHistory = useMemo(() => {
    let rows = history;
    if (selectedCurrency && wallets.length > 1) {
      rows = rows.filter((row) => (row.currency || "USDT") === selectedCurrency);
    }
    if (historyRound === "all") return rows;
    const round = Number(historyRound);
    return rows.filter((row) => Number(row.round) === round);
  }, [history, historyRound, selectedCurrency, wallets.length]);

  const handleCopyUuid = async () => {
    const ok = await copyText(user.uuid);
    if (ok) toast.success("UUID copied");
    else toast.error("Could not copy UUID");
  };

  const statusLabel = (status) => {
    if (status === "paid") return "Paid";
    if (status === "partial") return "Partial";
    if (status === "empty") return "Empty";
    return "Pending";
  };

  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <Link to="/dashboard/referral/round-releases">Round Release Ledger</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>User</span>
          </p>
          <h1 className="dashboard-hero__title">User Round Release Details</h1>
          <p className="dashboard-hero__subtitle">
            Vesting rounds: each round is a % of this user's total reward, credited to staking
          </p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-file-invoice" />
        </div>
      </section>

      <div className="dashboard-body">
        <div className="rrl-toolbar">
          <Link to="/dashboard/referral/round-releases" className="rrl-back">
            <i className="fas fa-arrow-left" /> Back to Round Release Ledger
          </Link>
        </div>

        {loading && !data ? (
          <div className="openorders-empty">
            <i className="fas fa-spinner fa-spin" /> Loading…
          </div>
        ) : error && !data ? (
          <div className="rrl-empty rrl-empty--error">
            <p>{error}</p>
            {canRetry ? (
              <button type="button" className="cp-btn cp-btn--primary" onClick={() => fetchDetail()}>
                Retry
              </button>
            ) : (
              <button type="button" className="cp-btn cp-btn--outline" onClick={goBack}>
                Back to list
              </button>
            )}
          </div>
        ) : data ? (
          <>
            <header className="rrl-user-head">
              <div>
                <h2>{userDisplayName(user)}</h2>
                <p>
                  {user.uuid ? (
                    <button type="button" className="rrl-uuid" onClick={handleCopyUuid}>
                      {user.uuid} <i className="fas fa-copy" />
                    </button>
                  ) : null}
                  {user.email ? <span>{user.email}</span> : null}
                  {user.phone ? <span>{user.phone}</span> : null}
                </p>
              </div>
              <span className={`rrl-badge rrl-badge--${String(summary.status || "").toLowerCase()}`}>
                {summary.status || "—"}
              </span>
            </header>

            <p className="rrl-note" role="note">
              <i className="fas fa-info-circle" /> {SEQUENCE_FALLBACK}
              {sequence.rule && sequence.rule !== SEQUENCE_FALLBACK ? (
                <span> {sequence.rule}</span>
              ) : null}
              <span className="rrl-note__dest">
                {" "}
                {sequence.destination || STAKING_WALLET_HINT}
              </span>
            </p>

            {wallets.length > 1 ? (
              <div className="rrl-chips" role="tablist" aria-label="Currency">
                {wallets.map((w, index) => {
                  const id = w.currency_id || w.currency;
                  const active =
                    selectedCurrencyId === id || (!selectedCurrencyId && index === 0);
                  return (
                    <button
                      key={id}
                      type="button"
                      className={`rrl-chip ${active ? "rrl-chip--active" : ""}`}
                      onClick={() => setSelectedCurrencyId(id)}
                    >
                      {w.currency}
                      {w.currency_fullname ? ` · ${w.currency_fullname}` : ""}
                    </button>
                  );
                })}
              </div>
            ) : null}

            <div className="rrl-cards">
              <article>
                <span>Total reward</span>
                <strong>{formatMoney(summary.total_reward, currencyCode)}</strong>
              </article>
              <article className="rrl-cards__paid">
                <span>Released</span>
                <strong>{formatMoney(summary.released_amount, currencyCode)}</strong>
              </article>
              <article className="rrl-cards__remain">
                <span>Remaining</span>
                <strong>{formatMoney(summary.remaining_amount, currencyCode)}</strong>
              </article>
              <article>
                <span>Next round</span>
                <strong>
                  {summary.next_round
                    ? `R${summary.next_round} · ${formatMoney(
                        summary.next_round_amount,
                        summary.next_round_currency || currencyCode
                      )}`
                    : "—"}
                </strong>
              </article>
              <article>
                <span>Staking wallet</span>
                <strong>{formatMoney(selectedWallet?.balance ?? 0, currencyCode)}</strong>
                <small>
                  {formatWalletType(selectedWallet?.wallet_type || "staking")} · purchase only
                </small>
              </article>
            </div>

            <div className="rrl-tabs">
              {TABS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`rrl-tab ${tab === item.id ? "rrl-tab--active" : ""}`}
                  onClick={() => setTab(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {polling ? (
              <p className="rrl-processing" role="status">
                <i className="fas fa-spinner fa-spin" /> Release in progress — paid amounts will update when the job finishes.
              </p>
            ) : null}

            {tab === "rounds" ? (
              visibleRoundGroups.length === 0 ? (
                <div className="rrl-empty">
                  <p>This user has no vesting plans</p>
                </div>
              ) : (
                <>
                  {visibleRoundGroups.map((group) => (
                    <section key={group.currency} className="rrl-round-block">
                      <header className="rrl-round-block__head">
                        <h3>{group.currency} rounds</h3>
                        <p>
                          Each round is a percentage of this user&apos;s total reward. Last round
                          pays leftover so the total is 100%. Coins are not mixed.
                        </p>
                      </header>
                      <div className="openorders-table-wrap">
                        <table className="openorders-table">
                          <thead>
                            <tr>
                              <th>Round</th>
                              <th>% of total</th>
                              <th>Window</th>
                              <th>Total due</th>
                              <th>Paid</th>
                              <th>Pending</th>
                              <th>Status</th>
                              <th>Wallet</th>
                              <th>Earnings</th>
                              <th>Currency</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.rows.map((row) => (
                              <tr
                                key={`${group.currency}-${row.round}`}
                                className={row.status === "pending" ? "rrl-row--pending" : ""}
                              >
                                <td>R{row.round}</td>
                                <td>
                                  {row.percentage != null ? `${row.percentage}%` : "—"}
                                  {group.rows[group.rows.length - 1]?.round === row.round ? (
                                    <small className="rrl-leftover"> leftover</small>
                                  ) : null}
                                </td>
                                <td>
                                  {row.from_month != null || row.to_month != null
                                    ? `${row.from_month ?? "—"}–${row.to_month ?? "—"}`
                                    : "—"}
                                </td>
                                <td>{formatMoney(row.total_due, row.currency)}</td>
                                <td className={row.paid > 0 ? "rrl-amt--paid" : ""}>
                                  {formatMoney(row.paid, row.currency)}
                                </td>
                                <td className={row.pending > 0 ? "rrl-amt--remain" : ""}>
                                  {formatMoney(row.pending, row.currency)}
                                  {row.pending > 0 ? <small className="rrl-will"> will go</small> : null}
                                </td>
                                <td>
                                  <span className={`rrl-badge rrl-badge--${row.status}`}>
                                    {statusLabel(row.status)}
                                  </span>
                                </td>
                                <td>
                                  {row.paid > 0
                                    ? formatWalletType(row.wallet_type || "staking")
                                    : "—"}
                                </td>
                                <td>{row.earning_count != null ? row.earning_count : "—"}</td>
                                <td>{row.currency}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </section>
                  ))}

                  <details className="rrl-by-earning">
                    <summary>
                      Earnings (source bonuses) <span>({visibleVestings.length})</span>
                    </summary>
                    <div className="rrl-vest-grid">
                      {visibleVestings.map((vesting) => {
                        const ccy = vesting.currency || currencyCode;
                        const reward = Number(vesting.reward_amount);
                        const paid =
                          String(vesting.status || "").toLowerCase() === "complete" ||
                          (Number(vesting.released_amount) >= reward && reward > 0);
                        return (
                          <article key={vesting.vesting_id} className="rrl-vest">
                            <header>
                              <div>
                                <strong>{earningAccordionTitle(vesting)}</strong>
                                {vesting.vesting_id ? (
                                  <p className="rrl-vest__id">{vesting.vesting_id}</p>
                                ) : null}
                                <p>
                                  Source bonus {formatMoney(vesting.reward_amount, ccy)}
                                  {vesting.created_at ? ` · ${formatDateTime(vesting.created_at)}` : ""}
                                </p>
                              </div>
                              <span className={`rrl-badge ${paid ? "rrl-badge--paid" : "rrl-badge--pending"}`}>
                                {paid ? "Paid" : vesting.status || "Pending"}
                              </span>
                            </header>
                          </article>
                        );
                      })}
                    </div>
                  </details>
                </>
              )
            ) : (
              <>
                <div className="rrl-chips rrl-chips--history" role="tablist" aria-label="History round">
                  {HISTORY_ROUND_FILTERS.map((item) => (
                    <button
                      key={String(item.value)}
                      type="button"
                      className={`rrl-chip ${historyRound === item.value ? "rrl-chip--active" : ""}`}
                      onClick={() => setHistoryRound(item.value)}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
                {filteredHistory.length === 0 ? (
                  <div className="rrl-empty">
                    <p>No paid payouts yet</p>
                  </div>
                ) : (
                  <div className="openorders-table-wrap">
                    <table className="openorders-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Round</th>
                          <th>Credit</th>
                          <th>Wallet</th>
                          <th>Source</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredHistory.map((row, idx) => {
                          const ccy = row.currency || currencyCode;
                          return (
                            <tr key={row.id || `${row.created_at}-${row.round}-${idx}`}>
                              <td>{formatDateTime(row.created_at)}</td>
                              <td>R{row.round}</td>
                              <td className="rrl-amt--paid">{formatMoney(row.amount, ccy)}</td>
                              <td>{formatWalletType(row.wallet_type || "staking")}</td>
                              <td>{releasedByLabel(row.released_by)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </>
        ) : null}
      </div>
    </main>
  );
};

export default RoundReleaseLedgerDetailPage;
